import { Controller } from "stimulus"
import { addCalendarDays, addWorkingDays, nextWorkingDay, toDateInput } from "../phone_dashboard/working_days"
import { DueDatePicker, dueDateLabel, openDueDatePicker, parseDateInput, startOfToday } from "../phone_dashboard/due_date_picker"

type Mode = "new" | "edit" | "follow-up"

interface TaskValues { id?: string; title: string; date: string; time: string; who: string; note: string }

interface OpenDetail {
  mode: Mode
  candidateId?: string
  candidateName?: string
  task?: Partial<TaskValues> & { whoEmail?: string; whoInitials?: string; recording?: string }
  outcome?: "no_response" | "maybe_later" | "follow_up_later"
  callNote?: string
  callLogId?: string
  // 06 waits on this: the dialog answers with a task to create, or null to continue without one.
  resolve?: (result: { cancelled?: boolean; task?: TaskValues | null }) => void
}

// Per-outcome prefill (§4). The mockup approximates the first two with calendar days; the
// handoff's working-day wording wins.
const PREFILL: Record<string, { title: (name: string) => string; due: () => Date }> = {
  no_response: { title: name => `Retry call: ${name}`, due: () => addWorkingDays(new Date(), 1) },
  maybe_later: { title: name => `Follow up (maybe later): ${name}`, due: () => addWorkingDays(new Date(), 2) },
  follow_up_later: { title: name => `Call back ${name}`, due: () => addCalendarDays(new Date(), 7) }
}

export default class extends Controller {
  static targets = [
    "heading", "sub", "title", "date", "dateButton", "dateLabel", "time", "assignee", "note",
    "attach", "error", "skip", "cancel", "delete", "submit", "submitLabel"
  ]
  static values = { url: String, me: String }

  declare readonly headingTarget: HTMLElement
  declare readonly subTarget: HTMLElement
  declare readonly titleTarget: HTMLInputElement
  declare readonly dateTarget: HTMLInputElement
  declare readonly dateButtonTarget: HTMLButtonElement
  declare readonly dateLabelTarget: HTMLElement
  declare readonly timeTarget: HTMLInputElement
  declare readonly assigneeTarget: HTMLInputElement
  declare readonly noteTarget: HTMLTextAreaElement
  declare readonly attachTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly skipTarget: HTMLButtonElement
  declare readonly cancelTarget: HTMLButtonElement
  declare readonly deleteTarget: HTMLButtonElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly submitLabelTarget: HTMLElement
  declare readonly urlValue: string
  declare readonly meValue: string

  private mode: Mode = "new"
  private candidateId: string | null = null
  private taskId: string | null = null
  private resolve: OpenDetail["resolve"] | null = null
  private sending = false
  private calendar: DueDatePicker | null = null
  // The due date and time the edited task opened with: keeping an overdue one is allowed.
  private openedDue = ""

  open(event: CustomEvent<OpenDetail>): void {
    const detail = event.detail
    if (!detail) return

    this.mode = detail.mode
    this.candidateId = detail.candidateId || null
    this.taskId = detail.task?.id || null
    this.resolve = detail.resolve || null
    this.sending = false
    this.clearError()

    const name = detail.candidateName || "this candidate"
    const prefill = detail.outcome ? PREFILL[detail.outcome] : null

    if (this.mode === "edit") {
      this.titleTarget.value = detail.task?.title || ""
      this.setDate(detail.task?.date || toDateInput(nextWorkingDay()))
      this.timeTarget.value = detail.task?.time || "10:00"
      this.noteTarget.value = detail.task?.note || ""
      this.select(detail.task?.who, detail.task?.whoEmail, detail.task?.whoInitials)
    } else {
      const due = prefill ? prefill.due() : nextWorkingDay()
      this.titleTarget.value = prefill ? prefill.title(name) : `Call ${name}`
      this.setDate(toDateInput(due))
      this.timeTarget.value = "10:00"
      this.noteTarget.value = detail.callNote || ""
      // You, if you are eligible: the picker drops an id it doesn't list (Q7c).
      this.select(this.meValue)
    }
    this.openedDue = this.mode === "edit" ? `${this.dateTarget.value}T${this.timeTarget.value}` : ""

    this.attachTarget.hidden = !detail.task?.recording
    if (detail.task?.recording) this.attachTarget.textContent = "Call recording attached"

    this.applyMode()
    this.element.classList.add("open")
    this.element.setAttribute("aria-hidden", "false")
    this.titleTarget.focus()
  }

  close(): void {
    if (this.sending) return

    this.dismiss()

    // A follow-up dialog that is dismissed logs nothing: 06 is waiting on this answer.
    const resolve = this.resolve
    this.resolve = null
    if (resolve) resolve({ cancelled: true })
  }

  closeOnBackdrop(event: Event): void {
    if (event.target === this.element) this.close()
  }

  // Esc closes an open calendar or assignee menu first, and the dialog only after that.
  closeOnEscape(event: KeyboardEvent): void {
    if (event.key !== "Escape") return
    if (!this.element.classList.contains("open")) return

    if (this.calendar?.isOpen()) {
      this.calendar.hide()
      return
    }

    const menu = this.element.querySelector(".task-dd.open")
    if (menu) {
      menu.dispatchEvent(new CustomEvent("task-assignee-picker:close"))
      return
    }

    this.close()
  }

  // Stopped here as on the Todo page: Litepicker's own document listener would close the calendar
  // on the click that opens it. That also keeps the click from the assignee menu, so close it too.
  pickDate(event: Event): void {
    event.stopPropagation()
    this.element.querySelector(".task-dd.open")?.dispatchEvent(new CustomEvent("task-assignee-picker:close"))

    if (this.calendar?.isOpen()) {
      this.calendar.hide()
      return
    }

    this.calendar = openDueDatePicker(this.dateTarget, this.dateButtonTarget, date => {
      this.setDate(date)
      this.clearError()
    })
  }

  // Follow-up mode: log the outcome without a task.
  skip(): void {
    const resolve = this.resolve
    this.resolve = null
    this.dismiss()
    if (resolve) resolve({ task: null })
  }

  submit(): void {
    if (this.sending) return

    const values = this.values()
    if (!values.title.trim()) return this.showError("Give the task a title.")
    if (!values.date) return this.showError("Give the task a due date.")
    if (!values.who) return this.showError("Choose who this task is for.")

    const pastDue = this.pastDueError(values)
    if (pastDue) return this.showError(pastDue)

    // 06 sends the task with the outcome in one request, so the dialog only hands it back.
    if (this.mode === "follow-up") {
      const resolve = this.resolve
      this.resolve = null
      this.dismiss()
      if (resolve) resolve({ task: values })
      return
    }

    const editing = this.mode === "edit" && this.taskId
    this.send(editing ? "PATCH" : "POST", editing ? `${this.urlValue}/${this.taskId}` : this.urlValue, {
      task: {
        bots_prospect_id: this.candidateId,
        title: values.title,
        due_at: `${values.date}T${values.time || "10:00"}`,
        assignee_id: values.who,
        note: values.note
      }
    })
  }

  destroy(): void {
    if (this.sending || !this.taskId) return

    this.calendar?.hide()
    if (!window.confirm("Delete this task?")) return

    this.send("DELETE", `${this.urlValue}/${this.taskId}`, null)
  }

  // --- internals ---------------------------------------------------------

  private send(method: string, url: string, body: unknown): void {
    this.setSending(true)

    fetch(url, {
      method,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: body ? JSON.stringify(body) : undefined
    })
      .then(response => response.json().then(data => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        this.setSending(false)

        // A 404 still carries the cell, so a task someone else removed is cleared from the row.
        if (data?.next_task_html) this.swapCell(data.next_task_html)

        if (!ok) {
          this.showError((data?.errors || [])[0] || "This task no longer exists.")
          return
        }

        this.dismiss()
        this.resolve = null
      })
      .catch(() => {
        this.setSending(false)
        this.showError("That didn't reach the server. Your changes are still here — try again.")
      })
  }

  private swapCell(html: string): void {
    if (!this.candidateId) return

    // every row for this candidate (grid and list panes can both render it)
    document.querySelectorAll<HTMLElement>(`tr[data-bp-id="${this.candidateId}"] .st-next`).forEach(cell => {
      cell.outerHTML = html
    })
  }

  private values(): TaskValues {
    return {
      id: this.taskId || undefined,
      title: this.titleTarget.value,
      date: this.dateTarget.value,
      time: this.timeTarget.value,
      who: this.assigneeTarget.value,
      note: this.noteTarget.value
    }
  }

  // The calendar lives on <body>, outside this element, so hiding the dialog doesn't hide it.
  private dismiss(): void {
    this.calendar?.hide()
    this.element.classList.remove("open")
    this.element.setAttribute("aria-hidden", "true")
  }

  private setDate(value: string): void {
    this.dateTarget.value = value
    this.dateLabelTarget.textContent = dueDateLabel(value)
  }

  // The calendar already disables past days; this catches today at an earlier time, and an
  // overdue task whose time was changed but whose date wasn't. The server checks it too.
  private pastDueError(values: TaskValues): string | null {
    if (this.openedDue && this.openedDue === `${values.date}T${values.time}`) return null

    const date = parseDateInput(values.date)
    if (!date || date < startOfToday()) return "Pick today or a later date."

    const [hours, minutes] = (values.time || "10:00").split(":").map(Number)
    date.setHours(hours, minutes, 0, 0)

    return date < new Date() ? "Pick a time later than now." : null
  }

  // An assignee who is no longer eligible still shows, by email, so an edit does not silently
  // reassign the task.
  private select(id?: string, email?: string, initials?: string): void {
    this.assigneeTarget.value = id || ""
    if (email) this.assigneeTarget.dataset.email = email
    else delete this.assigneeTarget.dataset.email
    if (initials) this.assigneeTarget.dataset.ini = initials
    else delete this.assigneeTarget.dataset.ini

    this.assigneeTarget.closest(".task-dd")?.dispatchEvent(new CustomEvent("task-assignee-picker:sync"))
  }

  private applyMode(): void {
    const followUp = this.mode === "follow-up"
    const editing = this.mode === "edit"

    this.headingTarget.textContent = editing ? "Edit call task" : followUp ? "Create follow-up task" : "Create call task"
    this.subTarget.textContent = followUp ? "A task keeps this candidate on your to-do list." : ""
    this.skipTarget.hidden = !followUp
    this.cancelTarget.hidden = followUp
    this.deleteTarget.hidden = !editing
    this.submitLabelTarget.textContent = editing ? "Save changes" : followUp ? "Create task & continue" : "Create task"
  }

  private setSending(sending: boolean): void {
    this.sending = sending
    this.submitTarget.disabled = sending
  }

  private showError(message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  private clearError(): void {
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }
}
